// Monthly WFH / leave allowances.
//
// Policy: an employee may take up to 4 work-from-home days and up to 2 leaves
// in a calendar month. Going over either limit does not block the request — it
// raises a warning (leave beyond the allowance may be deducted from salary), so
// this module only counts and reports; nothing here rejects a request.
//
// Counting rules (agreed with HR):
//   - working days only — weekends never count, matching workingDaysBetween()
//     which is what the UI already shows employees as a request's day count
//   - APPROVED requests only — a pending request has not been taken yet
//   - a request spanning a month boundary contributes its days to each month
//
// Isomorphic: imported by both the Express routes and the React dashboards, so
// keep it free of firebase/DOM dependencies.

import { isValidYMD, todayIST } from './date-utils'

export const WFH_MONTHLY_LIMIT = 4
export const LEAVE_MONTHLY_LIMIT = 2

/** Shown to the employee on their own WFH page. */
export const WFH_LIMIT_WARNING =
  'Warning: You have exceeded the maximum allowed 4 Work From Home days for this month.'

/** Shown to the employee on their own leave page. */
export const LEAVE_LIMIT_WARNING =
  'Warning: You have exceeded the allowed 2 leaves. Any additional leave may result in a salary deduction.'

// Shown while a request is still being filled in — it has not been taken yet,
// so it forecasts rather than reports.
export const WFH_LIMIT_FORECAST =
  `Heads up: this request would take you past the ${WFH_MONTHLY_LIMIT} Work From Home days allowed this month.`

export const LEAVE_LIMIT_FORECAST =
  `Heads up: this request would take you past the ${LEAVE_MONTHLY_LIMIT} leaves allowed this month. Any additional leave may result in a salary deduction.`

/** Same warning, addressed to an approver looking at someone else's request. */
export function wfhLimitWarningFor(name?: string | null): string {
  const who = name?.trim() || 'This employee'
  return `Warning: ${who} has exceeded the maximum allowed ${WFH_MONTHLY_LIMIT} Work From Home days for this month.`
}

/** Same warning, addressed to an approver looking at someone else's request. */
export function leaveLimitWarningFor(name?: string | null): string {
  const who = name?.trim() || 'This employee'
  return `Warning: ${who} has exceeded the allowed ${LEAVE_MONTHLY_LIMIT} leaves. Any additional leave may result in a salary deduction.`
}

/** 'YYYY-MM-DD' → 'YYYY-MM'. Returns '' for anything that isn't a date key. */
export function monthKey(ymd?: string | null): string {
  return isValidYMD(ymd) ? ymd.slice(0, 7) : ''
}

/** The current calendar month in IST — the timezone every date key uses. */
export function currentMonthKey(): string {
  return todayIST().slice(0, 7)
}

/** Human label for a 'YYYY-MM' key, e.g. 'July 2026'. */
export function formatMonthKey(month: string): string {
  if (!/^\d{4}-\d{2}$/.test(month)) return month
  const d = new Date(month + '-01T00:00:00Z')
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })
}

/**
 * Working days of [start, end] that fall inside `month` ('YYYY-MM').
 * A 28 Jun – 3 Jul request contributes only its July weekdays to '2026-07'.
 */
export function workingDaysInMonth(start: string, end: string, month: string): number {
  if (!isValidYMD(start) || !isValidYMD(end)) return 0
  const s = new Date(start + 'T00:00:00Z')
  const e = new Date(end + 'T00:00:00Z')
  if (e < s) return 0
  let count = 0
  for (let d = s; d <= e; d = new Date(d.getTime() + 86_400_000)) {
    const day = d.getUTCDay()
    if (day === 0 || day === 6) continue
    if (d.toISOString().slice(0, 7) !== month) continue
    count++
  }
  return count
}

export interface AllowanceRequest {
  startDate?: string | null
  endDate?: string | null
  status?: string | null
  userId?: string | null
}

function isApproved(r: AllowanceRequest): boolean {
  return String(r.status ?? '').toUpperCase() === 'APPROVED'
}

export function isPending(r: AllowanceRequest): boolean {
  return String(r.status ?? '').toUpperCase() === 'PENDING'
}

/** Approved working days taken in `month` across the given requests. */
export function monthlyDaysUsed(requests: AllowanceRequest[], month: string): number {
  return requests.reduce((sum, r) => {
    if (!isApproved(r) || !r.startDate || !r.endDate) return sum
    return sum + workingDaysInMonth(r.startDate, r.endDate, month)
  }, 0)
}

/** Same as monthlyDaysUsed, bucketed by userId — for the admin approval tables. */
export function monthlyDaysUsedByUser(requests: AllowanceRequest[], month: string): Record<string, number> {
  const out: Record<string, number> = {}
  for (const r of requests) {
    if (!isApproved(r) || !r.startDate || !r.endDate || !r.userId) continue
    const days = workingDaysInMonth(r.startDate, r.endDate, month)
    if (!days) continue
    out[r.userId] = (out[r.userId] || 0) + days
  }
  return out
}

/**
 * Approved days indexed by `${userId}|${month}` — one pass over every request,
 * so an approval table can look up any row's month without re-scanning. A
 * request straddling a month boundary contributes to both months.
 */
export function monthlyUsageIndex(requests: AllowanceRequest[]): Record<string, number> {
  const out: Record<string, number> = {}
  for (const r of requests) {
    if (!isApproved(r) || !r.startDate || !r.endDate || !r.userId) continue
    const months = new Set([monthKey(r.startDate), monthKey(r.endDate)].filter(Boolean))
    for (const m of months) {
      const days = workingDaysInMonth(r.startDate, r.endDate, m)
      if (!days) continue
      const key = `${r.userId}|${m}`
      out[key] = (out[key] || 0) + days
    }
  }
  return out
}

export function usageFrom(index: Record<string, number>, userId?: string | null, month?: string | null): number {
  if (!userId || !month) return 0
  return index[`${userId}|${month}`] || 0
}

/**
 * Days used in the month a request falls in, plus the request's own days when it
 * is still pending — what the approver is about to commit to. Approving a
 * request that pushes the total past the limit is exactly what HR needs flagged.
 * Rejected requests add nothing: they were never taken.
 */
export function projectedDaysForRequest(
  request: AllowanceRequest,
  allRequestsForUser: AllowanceRequest[],
  month = monthKey(request.startDate)
): number {
  const used = monthlyDaysUsed(allRequestsForUser, month)
  if (!isPending(request) || !request.startDate || !request.endDate) return used
  return used + workingDaysInMonth(request.startDate, request.endDate, month)
}

export function isOverWfhLimit(days: number): boolean {
  return days > WFH_MONTHLY_LIMIT
}

export function isOverLeaveLimit(days: number): boolean {
  return days > LEAVE_MONTHLY_LIMIT
}
